import {BrowserRouter as Router, Route, Routes, Navigate} from 'react-router-dom';
import Layout from "./components/Layout";
import {
  DocumentIcon,
} from '@heroicons/react/24/outline';
import Dashboard from './pages/admin/Dashboard';
import Users from './pages/admin/users/Users';
import SignIn from "./pages/authentication/SignIn";
import SignUp from "./pages/authentication/SignUp";
import Welcome from "./pages/authentication/Welcome";
import Settings from "./pages/admin/SettingsMain";
import ResourcesPage from "./pages/admin/data/resources/ResourcesMain";
import ResourceParticipants from "./pages/admin/data/resources/ResourceParticipants";
import ChallengeTemplates from "./pages/admin/data/resources/templates/ChallengeTemplates";
import Operations from "./pages/admin/Operations";
import PrivateRoute from "./routes/PrivateRoute";
import AdminRoute from "./routes/AdminRoute";
import ForgotPassword from "./pages/authentication/ForgotPassword";
import ApplyToJoin from "./pages/ApplyToJoin";
import LegalPage from "./pages/LegalPage";
import SetPassword from "./pages/authentication/SetPassword";
import EmailVerification from "./pages/authentication/EmailVerification";
import Homepage from "./pages/Homepage";
import About from "./pages/About";
import Markets from "./pages/Markets";
import PlaygroundInfo from "./pages/PlaygroundInfo";
import SessionsPage from "./pages/admin/sessions/index";
import SchedulePage from "./pages/admin/schedule/index";
import PaymentsPage from "./pages/admin/users/paymentsPage/Payments";
import PaymentGroupsPage from "./pages/admin/users/paymentGroupsPage/PaymentGroups";
import ExcludedDays from "./pages/admin/excludedDays/ExcludedDays";
import Notices from "./pages/admin/notices/Notices";
import NoticesPage from "./pages/notices/NoticesPage";
import EmailHistoryPage from "./pages/admin/users/emailHistoryPage/EmailHistory";
import FilesPage from "./pages/admin/data/files/Files";
import ReportsPage from "./pages/admin/data/reports/Reports";
import InviteUser from "./pages/admin/users/InviteUser";
import UserProfile from "./pages/admin/users/UserProfile";
import UserActivity from "./pages/admin/users/UserActivity";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";
import Documentation from "./pages/Documentation";
import Changelog from "./pages/Changelog";
import {ToastContainer} from 'react-toastify';
import {Slide} from "react-toastify";
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectTheme } from './slices/preferencesSlice';
import {
  fetchManagedResourceRoles,
  selectAuthIsAuth,
  selectAuthIsForecaster,
  selectManagedResourcesAuth
} from './slices/authSlice';
import { buildNavigation } from './config/navigation/buildNavigation';


export default function App() {
  const dispatch = useDispatch();
  const theme = useSelector(selectTheme);
  const isAuth = useSelector(selectAuthIsAuth);
  const isForecaster = useSelector(selectAuthIsForecaster);
  const managedResources = useSelector(selectManagedResourcesAuth);
  const [effectiveDark, setEffectiveDark] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const compute = () => setEffectiveDark(theme === 'dark' || (theme === 'system' && mediaQuery.matches));
    compute();
    const listener = () => theme === 'system' && compute();
    mediaQuery.addEventListener ? mediaQuery.addEventListener('change', listener) : mediaQuery.addListener(listener);
    return () => {
      mediaQuery.removeEventListener ? mediaQuery.removeEventListener('change', listener) : mediaQuery.removeListener(listener);
    };
  }, [theme]);

  // Fetch managed resources for non-forecasters when authenticated
  useEffect(() => {
    if (isAuth && !isForecaster && managedResources.length === 0) {
      dispatch(fetchManagedResourceRoles());
    }
  }, [isAuth, isForecaster, managedResources.length, dispatch]);

  // Navigation and other state data
  const navigation = buildNavigation({ isForecaster });

  // react-toastify hard-codes its own centered-container breakpoint at
  // max-width:480px, below Tailwind's sm (640px, unconfigured). Between
  // those two thresholds the container falls back to its native fixed
  // --toastify-toast-width (320px default), so a toast sized purely through
  // toastClassName either overflows that gap or leaves it uncentered.
  // Driving the container's own width variable instead keeps its built-in
  // centering correct at every width; the toast then just fills it. !px-0
  // removes the container's 4px side padding so the toast (w-full above
  // 480px) matches the variable exactly instead of coming in 8px short.
  return (
    <Router>
      <AppContent navigation={navigation}/>
      <ToastContainer
        className="!px-0"
        style={{ '--toastify-toast-width': 'min(500px, calc(100vw - 2rem))' }}
        toastClassName="w-[calc(100vw-2rem)] mx-auto min-[481px]:w-full rounded-lg shadow-lg"
        hideProgressBar
        autoClose={2000}
        closeOnClick
        theme={effectiveDark ? 'dark' : 'light'}
        position="top-center"
        transition={Slide}
      />

    </Router>
  );
}

// Create a separate component that can use the Auth context
function AppContent({navigation}) {

  // User navigation with logout functionality
  const userNavigation = [
    {name: 'Settings', href: '/settings', icon: DocumentIcon},
    {name: 'Sign out', href: '/logout', icon: DocumentIcon},
  ];

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Homepage/>}/>
      <Route path="/about" element={<About/>}/>
      <Route path="/markets" element={<Markets/>}/>
      <Route path="/playground-info" element={<PlaygroundInfo/>}/>
      <Route path="/signin" element={<SignIn/>}/>
      <Route path="/signup/:token" element={<SignUp/>}/>
      <Route path="/welcome" element={<Welcome/>}/>
      <Route path="/forgot-password" element={<ForgotPassword/>}/> {/* Add ForgotPassword route */}
      <Route path="/apply" element={<ApplyToJoin/>}/>
      <Route path="/terms" element={<LegalPage document="terms"/>}/>
      <Route path="/privacy" element={<LegalPage document="privacy"/>}/>
      <Route path="/set-password/:token" element={<SetPassword/>}/> {/* Add ForgotPassword route */}
      <Route path={"/email-verification/:uidb64/:token"}
             element={<EmailVerification/>}/> {/* Add EmailVerification route */}

      {/* Private routes */}
      <Route element={<PrivateRoute/>}>
        <Route element={<Layout navigation={navigation} userNavigation={userNavigation}/>}>

          {/* Now wrap certain routes with ResourceGuard */}
          {/*<Route element={<ResourceGuard />}>*/}
          <Route path="/dashboard" element={<Dashboard/>}/>
          <Route path="/settings" element={<Settings/>}/>
          <Route path="/forecasts" element={<SessionsPage/>}/>
          <Route path="/schedule" element={<SchedulePage/>}/>
          <Route path="/sessions" element={<Navigate to="/forecasts" replace />}/>
          {/*</Route>*/}

          <Route path={"/data/resource"} element={<ResourcesPage/>}/>
          <Route path={"/files"} element={<FilesPage/>}/>
          <Route path={"/analytics"} element={<ReportsPage/>}/>
          <Route element={<AdminRoute requiredPermission={["market_owner_read","market_owner","admin"]}/> }>
            <Route path={"/admin-documentation"} element={<Documentation mode="admin"/>}/>
          </Route>
          <Route path={"/changelog"} element={<Changelog/>}/>
          <Route path={"/notices"} element={<NoticesPage/>}/>
          <Route path={"/faq"} element={<FAQ/>}/>
          <Route path={"/data/files"} element={<Navigate to="/files" replace />}/>
          <Route path={"/data/reports"} element={<Navigate to="/analytics" replace />}/>
          <Route path={"/reports"} element={<Navigate to="/analytics" replace />}/>
          <Route element={<AdminRoute requiredPermission={["market_owner_read","market_owner","admin"]}/> }>
            <Route path="/data/resource/challenge-templates" element={<ChallengeTemplates/>}/>
            <Route path="/users/:userId" element={<UserProfile/>}/>
            <Route path="/users/invite-user" element={<InviteUser/>}/>
            <Route path={"/users/payments"} element={<PaymentsPage/>}/>
          </Route>
          <Route element={<AdminRoute requiredPermission="admin"/> }>
            <Route path="/operations" element={<Operations/>}/>
            <Route path="/operations/excluded-days" element={<ExcludedDays/>}/>
            <Route path="/operations/notices" element={<Notices/>}/>
            <Route path="/users/list-users" element={<Users/>}/>
            <Route path="/users/activity" element={<UserActivity/>}/>
            <Route path="/data/resource/participants" element={<ResourceParticipants/>}/>
            <Route path={"/users/payment-groups"} element={<PaymentGroupsPage/>}/>
            <Route path={"/users/email-history"} element={<EmailHistoryPage/>}/>
          </Route>
        </Route>
      </Route>

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFound/>}/>

    </Routes>
  );
}
